import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  AcademicCapIcon,
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
} from '@heroicons/react/24/outline';

const CapabilitiesAssessmentForm = ({ data = {}, onSave, userRole }) => {
  const [assessmentData, setAssessmentData] = useState({
    businessSkills: data.businessSkills || {},
    technicalSkills: data.technicalSkills || {},
    leadershipSkills: data.leadershipSkills || {},
    financialSkills: data.financialSkills || {},
    marketingSkills: data.marketingSkills || {},
    operationalSkills: data.operationalSkills || {},
    score: data.score || 0,
    notes: data.notes || '',
  });

  const [saving, setSaving] = useState(false);

  const businessSkillsQuestions = [
    {
      id: 'businessPlanning',
      question: 'I can develop comprehensive business plans and strategies',
      category: 'businessSkills',
    },
    {
      id: 'marketAnalysis',
      question: 'I can analyze market conditions and competitive landscapes',
      category: 'businessSkills',
    },
    {
      id: 'businessModeling',
      question: 'I understand and can design business models',
      category: 'businessSkills',
    },
    {
      id: 'valueProposition',
      question: 'I can clearly articulate unique value propositions',
      category: 'businessSkills',
    },
    {
      id: 'goToMarket',
      question: 'I can develop go-to-market strategies',
      category: 'businessSkills',
    },
  ];

  const technicalSkillsQuestions = [
    {
      id: 'digitalLiteracy',
      question: 'I am proficient with digital tools and technologies',
      category: 'technicalSkills',
    },
    {
      id: 'dataAnalysis',
      question: 'I can analyze data to make informed business decisions',
      category: 'technicalSkills',
    },
    {
      id: 'technologyAdoption',
      question: 'I quickly adapt to new technologies and digital platforms',
      category: 'technicalSkills',
    },
    {
      id: 'automation',
      question: 'I can identify opportunities for process automation',
      category: 'technicalSkills',
    },
    {
      id: 'cybersecurity',
      question: 'I understand basic cybersecurity principles for business',
      category: 'technicalSkills',
    },
  ];

  const leadershipSkillsQuestions = [
    {
      id: 'teamBuilding',
      question: 'I can build and manage effective teams',
      category: 'leadershipSkills',
    },
    {
      id: 'decisionMaking',
      question: 'I make timely and effective decisions under pressure',
      category: 'leadershipSkills',
    },
    {
      id: 'communication',
      question: 'I communicate clearly and persuasively with stakeholders',
      category: 'leadershipSkills',
    },
    {
      id: 'conflictResolution',
      question: 'I can resolve conflicts and manage difficult situations',
      category: 'leadershipSkills',
    },
    {
      id: 'changeManagement',
      question: 'I can lead organizational change and transformation',
      category: 'leadershipSkills',
    },
  ];

  const financialSkillsQuestions = [
    {
      id: 'financialPlanning',
      question: 'I can develop financial forecasts and budgets',
      category: 'financialSkills',
    },
    {
      id: 'cashFlowManagement',
      question: 'I understand and can manage cash flow effectively',
      category: 'financialSkills',
    },
    {
      id: 'fundraising',
      question: 'I can prepare for and execute fundraising activities',
      category: 'financialSkills',
    },
    {
      id: 'financialAnalysis',
      question: 'I can read and interpret financial statements',
      category: 'financialSkills',
    },
    {
      id: 'investmentAnalysis',
      question: 'I can evaluate investment opportunities and risks',
      category: 'financialSkills',
    },
  ];

  const marketingSkillsQuestions = [
    {
      id: 'brandDevelopment',
      question: 'I can develop and manage brand identity',
      category: 'marketingSkills',
    },
    {
      id: 'digitalMarketing',
      question: 'I understand digital marketing channels and strategies',
      category: 'marketingSkills',
    },
    {
      id: 'customerAcquisition',
      question: 'I can develop customer acquisition strategies',
      category: 'marketingSkills',
    },
    {
      id: 'salesSkills',
      question: 'I have strong sales and negotiation skills',
      category: 'marketingSkills',
    },
    {
      id: 'customerRetention',
      question: 'I can develop customer retention strategies',
      category: 'marketingSkills',
    },
  ];

  const operationalSkillsQuestions = [
    {
      id: 'processOptimization',
      question: 'I can design and optimize business processes',
      category: 'operationalSkills',
    },
    {
      id: 'supplyChain',
      question: 'I understand supply chain management principles',
      category: 'operationalSkills',
    },
    {
      id: 'qualityManagement',
      question: 'I can implement quality management systems',
      category: 'operationalSkills',
    },
    {
      id: 'projectManagement',
      question: 'I can manage projects effectively from start to finish',
      category: 'operationalSkills',
    },
    {
      id: 'vendorManagement',
      question: 'I can manage relationships with suppliers and vendors',
      category: 'operationalSkills',
    },
  ];

  const handleResponseChange = (category, questionId, response) => {
    setAssessmentData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [questionId]: response,
      },
    }));
  };

  const calculateScore = () => {
    let totalPoints = 0;
    let maxPoints = 0;

    const categories = [
      'businessSkills',
      'technicalSkills',
      'leadershipSkills',
      'financialSkills',
      'marketingSkills',
      'operationalSkills',
    ];

    categories.forEach(category => {
      const categoryData = assessmentData[category];
      Object.values(categoryData).forEach(response => {
        if (response === 'expert') {
          totalPoints += 4;
        } else if (response === 'proficient') {
          totalPoints += 3;
        } else if (response === 'basic') {
          totalPoints += 2;
        } else if (response === 'beginner') {
          totalPoints += 1;
        }
        maxPoints += 4;
      });
    });

    return Math.round((totalPoints / maxPoints) * 100);
  };

  const handleSave = async () => {
    setSaving(true);
    const updatedData = {
      ...assessmentData,
      score: calculateScore(),
      lastUpdated: new Date().toISOString(),
    };

    try {
      await onSave(updatedData);
    } finally {
      setSaving(false);
    }
  };

  const getScoreColor = score => {
    if (score >= 75) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 45) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreStatus = score => {
    if (score >= 75) return 'Strong Skill Set';
    if (score >= 60) return 'Good Skill Foundation';
    if (score >= 45) return 'Developing Skills';
    return 'Needs Skill Development';
  };

  const renderQuestionSection = (title, questions, category) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
    >
      <h3 className="mb-6 text-xl font-semibold text-gray-900">{title}</h3>

      <div className="space-y-4">
        {questions.map((question, index) => (
          <div key={question.id} className="border-b border-gray-100 pb-4 last:border-b-0">
            <p className="mb-3 text-sm font-medium text-gray-900">
              {index + 1}. {question.question}
            </p>

            <div className="flex flex-wrap gap-2">
              {[
                { value: 'expert', label: 'Expert', icon: CheckCircleIcon, color: 'green' },
                { value: 'proficient', label: 'Proficient', icon: CheckCircleIcon, color: 'blue' },
                { value: 'basic', label: 'Basic', icon: MinusCircleIcon, color: 'gray' },
                { value: 'beginner', label: 'Beginner', icon: XCircleIcon, color: 'red' },
              ].map(({ value, label, icon: Icon, color }) => (
                <button
                  key={value}
                  onClick={() => handleResponseChange(category, question.id, value)}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                    assessmentData[category][question.id] === value
                      ? `bg-${color}-100 border-${color}-500 text-${color}-700`
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-purple-200 bg-purple-50 p-6">
        <div className="mb-4 flex items-center gap-3">
          <AcademicCapIcon className="h-8 w-8 text-purple-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Capabilities Assessment</h2>
            <p className="text-gray-600">
              Evaluate your skills and competencies across key business areas
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-600">Current Score</div>
            <div className={`text-3xl font-bold ${getScoreColor(calculateScore())}`}>
              {calculateScore()}%
            </div>
            <div className="text-sm text-gray-600">{getScoreStatus(calculateScore())}</div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-purple-600 px-6 py-3 font-medium text-white transition-colors hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Assessment'}
          </button>
        </div>
      </div>

      {renderQuestionSection('Business Skills', businessSkillsQuestions, 'businessSkills')}

      {renderQuestionSection('Technical Skills', technicalSkillsQuestions, 'technicalSkills')}

      {renderQuestionSection('Leadership Skills', leadershipSkillsQuestions, 'leadershipSkills')}

      {renderQuestionSection('Financial Skills', financialSkillsQuestions, 'financialSkills')}

      {renderQuestionSection('Marketing Skills', marketingSkillsQuestions, 'marketingSkills')}

      {renderQuestionSection('Operational Skills', operationalSkillsQuestions, 'operationalSkills')}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <h3 className="mb-4 text-xl font-semibold text-gray-900">Assessment Notes</h3>
        <textarea
          value={assessmentData.notes}
          onChange={e => setAssessmentData(prev => ({ ...prev, notes: e.target.value }))}
          rows={4}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-purple-500"
          placeholder="Add any additional observations about your capabilities, skill gaps, or development priorities..."
        />
      </motion.div>
    </div>
  );
};

export default CapabilitiesAssessmentForm;
